import { FiltersDefinition, FiltersState } from '@factorialco/f0-react/dist/experimental'
import { AnalyticsReportResultsQuery } from 'generated/resources/graphql'

type Schema =
  AnalyticsReportResultsQuery['iris']['sqlResultsConnection']['nodes'][number]['schema'][number]

function filterForType(column: Schema): FiltersDefinition<string> {
  const label = column.displayName ?? column.name
  if (column.enumValues.length > 0) {
    return {
      [column.name]: {
        type: 'in',
        label,
        options: {
          options: column.enumValues.map((value) => ({ value, label: value })),
        },
      },
    }
  }

  if (column.dataType === 'string' && column.formatHint === 'date') {
    return { [column.name]: { type: 'date', options: { mode: 'range' }, label } }
  }

  if (column.dataType === 'string') {
    return { [column.name]: { type: 'search', label } }
  }

  return {}
}

export function filterDefinitionsFromSchema(schema: Schema[]): FiltersDefinition<string> {
  return schema.map(filterForType).reduce((acc, curr) => ({ ...acc, ...curr }), {})
}

type JsonLogicOperator =
  | 'var'
  | 'missing'
  | 'missing_some'
  | 'if'
  | '=='
  | '==='
  | '!='
  | '!=='
  | '!'
  | '!!'
  | 'or'
  | 'and'
  | '>'
  | '>='
  | '<'
  | '<='
  | 'between'
  | 'max'
  | 'min'
  | '+'
  | '-'
  | '*'
  | '/'
  | '%'
  | 'map'
  | 'filter'
  | 'reduce'
  | 'all'
  | 'none'
  | 'some'
  | 'merge'
  | 'in'
  | 'cat'
  | 'substr'
  | 'log'

type JsonLogicFilter = {
  [K in JsonLogicOperator]?: JsonLogicFilter | unknown | Array<JsonLogicFilter | unknown>
}

export function datatableFiltersToJsonLogicFilters(
  schema: Schema[],
  filters: FiltersState<FiltersDefinition<string>>
): JsonLogicFilter | undefined {
  const conditions = Object.entries(filters).map(([fieldName, filterValue]) => {
    const column = schema.find((c) => c.name === fieldName)

    if (!column) {
      throw new Error(`Column ${fieldName} not found in schema`)
    }

    if (column.enumValues.length > 0) {
      return { in: [{ var: fieldName }, [filterValue ?? ''].flat()] }
    }

    if (column.dataType === 'string' && column.formatHint === 'date') {
      // @ts-expect-error - filterValue is guaranteed to be a DateRange but there's no way to type it
      return { between: [filterValue?.from, { var: fieldName }, filterValue?.to] }
    }

    if (column.dataType === 'string') {
      return { in: [filterValue ?? '', { var: fieldName }] }
    }

    throw new Error(`Unsupported column type ${column.dataType} for column ${fieldName}`)
  })

  if (conditions.length === 0) {
    return undefined
  }

  return { and: conditions }
}
